import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const NEEDS = ["AAC Device", "Sensory Tools", "ABA Therapy", "Speech Therapy", "OT", "Mobility Equipment", "Safety Products", "Bedding"];

export default function Insurance() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", insurance_provider: "",
    member_id: "", state: "", child_name: "", diagnosis: "ASD",
    needs: [], additional_info: ""
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const toggleNeed = (n) => setForm((s) => ({
    ...s, needs: s.needs.includes(n) ? s.needs.filter((x) => x !== n) : [...s.needs, n]
  }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/insurance", form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="insurance-success">
      <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
      <h1 className="font-display text-3xl font-bold mt-4">Inquiry received</h1>
      <p className="text-slate-600 mt-2">Our team will review your insurance details and contact you within 2 business days with what your plan may cover for autism products and therapy tools.</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="insurance-page">
      <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Insurance Support</h1>
      <p className="text-slate-600 mt-2">Sign up to see if your insurance may cover autism-related products, therapy tools, AAC devices, safety equipment, and mobility items.</p>

      <form onSubmit={submit} className="card-soft p-6 mt-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Your Full Name *</Label><Input data-testid="ins-full_name" required value={form.full_name} onChange={change("full_name")} /></div>
          <div><Label>Email *</Label><Input data-testid="ins-email" required type="email" value={form.email} onChange={change("email")} /></div>
          <div><Label>Phone *</Label><Input data-testid="ins-phone" required value={form.phone} onChange={change("phone")} /></div>
          <div><Label>State *</Label><Input data-testid="ins-state" required value={form.state} onChange={change("state")} /></div>
          <div><Label>Insurance Provider *</Label><Input data-testid="ins-provider" required value={form.insurance_provider} onChange={change("insurance_provider")} placeholder="e.g. Blue Cross, Aetna, Medicaid" /></div>
          <div><Label>Member ID (optional)</Label><Input data-testid="ins-member_id" value={form.member_id} onChange={change("member_id")} /></div>
          <div><Label>Child's Name *</Label><Input data-testid="ins-child" required value={form.child_name} onChange={change("child_name")} /></div>
          <div><Label>Diagnosis *</Label><Input data-testid="ins-diagnosis" required value={form.diagnosis} onChange={change("diagnosis")} /></div>
        </div>
        <div>
          <Label>Items you'd like coverage for</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {NEEDS.map((n) => (
              <label key={n} className="flex items-center gap-2 text-sm bg-pink-50/60 px-3 py-2 rounded-xl cursor-pointer">
                <Checkbox data-testid={`ins-need-${n}`} checked={form.needs.includes(n)} onCheckedChange={() => toggleNeed(n)} /> {n}
              </label>
            ))}
          </div>
        </div>
        <div><Label>Anything else?</Label><Textarea data-testid="ins-info" rows={3} value={form.additional_info} onChange={change("additional_info")} /></div>
        <button data-testid="ins-submit" disabled={busy} className="btn-pink">{busy ? "Submitting..." : "Submit Inquiry"}</button>
      </form>
    </div>
  );
}
